import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { patchSettings, settingsQueryKey } from '@/hooks/useSettings';
import { TEMPLATE_DEFAULTS_QUERY_KEY } from '@/hooks/useTemplateDefaults';
import { readGateExitNextParam } from '@/lib/safeNextPath';
import { toCompleteOnboardingRequest } from '@/services/onboarding/onboarding.mappers';
import type { CompleteOnboardingOptions, OnboardingToastCopy } from '@/services/onboarding/onboarding.types';
import { useHasNoSessions } from '@/services/onboarding/useHasNoSessions';
import { onboardingStatusQueryKey } from '@/services/onboarding/useOnboardingStatus';
import { statusAccountId } from '@/services/statusGate/useStatusGate';
import type { OnboardingProfile } from '@/types/onboarding';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { useLocation } from 'wouter';

const SCHEDULE_TUTORIAL_TIMEOUT_MS = 5_000;

const completeOnboarding = async (profile: OnboardingProfile): Promise<void> => {
  const response = await fetch('/api/onboarding/complete', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(toCompleteOnboardingRequest(profile)),
  });
  if (!response.ok) throw new Error('Failed to complete onboarding');
};

/** Best effort: without the flag the person just never sees the session tips. */
const scheduleSessionTutorial = async (): Promise<void> => {
  try {
    await patchSettings({ session_tutorial: 'pending' }, AbortSignal.timeout(SCHEDULE_TUTORIAL_TIMEOUT_MS));
  } catch {
    console.warn('Could not schedule the session tutorial');
  }
};

export const useCompleteOnboarding = (toastCopy: OnboardingToastCopy, { isFirstRun }: CompleteOnboardingOptions) => {
  const { user, refetch } = useAuth();
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();
  // Synchronous in-flight latch: two clicks can land before a re-render disables the button.
  const inFlight = useRef(false);
  // The tour is only for someone new to Scribe: a first onboarding AND no session yet. Onboarding is
  // also asked of existing users who never did it, so the first run alone is not enough. Loaded while
  // they answer, so finishing never waits on it; unknown (loading, failed) counts as not new.
  const { data: hasNoSessions } = useHasNoSessions({ accountId: statusAccountId(user), enabled: isFirstRun });
  const isNewToScribe = isFirstRun && hasNoSessions === true;

  const mutation = useMutation({
    mutationFn: completeOnboarding,
    onSuccess: async () => {
      // Not awaited: navigation never waits on it; refetch settings once it has landed.
      if (isNewToScribe) {
        void scheduleSessionTutorial().then(() =>
          queryClient.invalidateQueries({ queryKey: settingsQueryKey(statusAccountId(user)) })
        );
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: onboardingStatusQueryKey(statusAccountId(user)) }),
        queryClient.invalidateQueries({ queryKey: settingsQueryKey(statusAccountId(user)) }),
        // Completion copies starter templates and may set new defaults.
        queryClient.invalidateQueries({ queryKey: ['templates'] }),
        queryClient.invalidateQueries({ queryKey: TEMPLATE_DEFAULTS_QUERY_KEY }),
      ]);
      await refetch();
      // A gate redirect's `next` wins. Otherwise someone new goes straight to a new session for the tour; the PII training guard there takes
      // over first if training is still required. Everyone else lands home as before.
      navigate(readGateExitNextParam(window.location.search) ?? (isNewToScribe ? '/session/new' : '/'));
    },
    onError: () => {
      console.error('Error completing onboarding');
      toast({ title: toastCopy.errorTitle, description: toastCopy.errorDescription, variant: 'destructive' });
    },
  });

  const submit = (profile: OnboardingProfile) => {
    if (inFlight.current) return;
    inFlight.current = true;
    mutation.mutate(profile, {
      onError: () => {
        inFlight.current = false;
      },
    });
  };

  return {
    submit,
    isPending: mutation.isPending,
    // The POST succeeded: never allow a second submission from this mount.
    isSubmitted: mutation.isSuccess,
  };
};
